import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import type { Location } from '../../domain/models/locations/Location';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import EventInformation from './EventInformation';
import FootprintSection from './FootprintSection';
import LocationInformation from './LocationInformation';
import LocationRegisterCard from './LocationRegisterCard';
import PlaceInformation from './PlaceInformation';

export type OfflineLocationListVariant = 'bottomSheet' | 'listView';

interface OfflineLocationListContentProps {
  positions: Location[];
  artistId: string;
  variant?: OfflineLocationListVariant;
}

const OfflineLocationListItem = ({ pos }: { pos: Location }) => {
  const { colorMode } = useColorMode();
  const isEmptyFootprints = isEmpty(pos.footprints);
  const key = `${pos.latitude}-${pos.longitude}`;

  const BoxContent = () => (
    <>
      <PlaceInformation pos={pos} hasPadding={true} />
      <div
        className="mt-[14px] mb-[16px] !border-separator-light-1 dark:!border-separator-dark-1"
        style={{ border: '1px solid' }}
      />
      <div>
        <div className="flex flex-col">
          {pos.events.map((event, index) => (
            <button
              type="button"
              key={event.eventId}
              className="w-full text-left"
              onClick={() => {
                openNewTab(`/locations/${pos.locationId}/events/${event.eventId}`);
              }}
            >
              <EventInformation event={event} siteType={pos.siteType} hasPadding={true} />
              {index < pos.events.length - 1 && (
                <div className="my-[12px] border border-solid !border-separator-light-1 dark:!border-separator-dark-1" />
              )}
            </button>
          ))}
        </div>
        {(pos.externalSources.length > 0 || pos.footprints.length > 0) &&
          pos.events.length === 0 && <FootprintSection pos={pos} isListItem={true} />}
        {pos.events.length === 0 &&
          pos.externalSources.length === 0 &&
          pos.footprints.length === 0 && <LocationInformation pos={pos} />}
      </div>
    </>
  );

  const boxShadow =
    colorMode === 'dark'
      ? '0px 0px 2px 0 rgba(0, 0, 0, 0.20), 0px 0px 30px 0 rgba(0, 0, 0, 0.65)'
      : 'rgba(0, 0, 0, 0.01) 0px 0px 0px 1px, rgba(0, 0, 0, 0.08) 0px 0px 14px 0px';

  return isEmptyFootprints ? (
    <div
      key={key}
      className="mx-auto w-[calc(100%-48px)] rounded-xl mb-[12px] pb-[16px] pt-[14px] bg-background-light dark:bg-background-elevated"
      style={{ placeSelf: 'center', boxShadow }}
    >
      <BoxContent />
    </div>
  ) : (
    <div
      key={key}
      className="mx-auto w-[calc(100%-48px)] rounded-xl mb-[12px]"
      style={{
        placeSelf: 'center',
        padding: '1px',
        background: 'linear-gradient(to right bottom, #15CCCB, #4987FF, #8B1CFA)',
        boxShadow
      }}
    >
      <div className="pb-[16px] pt-[14px] bg-background-light dark:bg-background-elevated rounded-[11px]">
        <BoxContent />
      </div>
    </div>
  );
};

const OfflineLocationListContent = ({
  positions,
  artistId,
  variant = 'bottomSheet'
}: OfflineLocationListContentProps) => {
  const isListView = variant === 'listView';
  const locationCardMarginBottom = isListView ? 'mb-[16px]' : 'mb-[27px]';

  if (positions.length > 0) {
    return (
      <div
        className="pt-[10px]"
        style={
          isListView
            ? { paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))' }
            : undefined
        }
      >
        {positions.map((pos) => (
          <OfflineLocationListItem key={pos.locationId} pos={pos} />
        ))}
        <div className={`mt-[10px] ${locationCardMarginBottom}`}>
          <LocationRegisterCard artistId={artistId} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col items-center gap-[14px] text-center ${
        isListView ? 'justify-center min-h-full' : ''
      }`}
      style={isListView ? { padding: '24px 20px' } : { margin: 'auto 0' }}
    >
      <img
        src="/assets/icons/graphic-3d/3D.exclamation.png"
        alt=""
        className="w-[73px] h-[82px]"
      />
      <div className="flex flex-col items-center gap-[8px]">
        <p className="text-[17px] font-bold text-text-main-lv2 leading-none">
          {t('OfflineLocation.emptyLocationTitle')}
        </p>
        <p className="text-[14px] text-text-main-lv4 font-medium leading-tight whitespace-pre-line">
          {t('OfflineLocation.emptyLocationDescription')}
        </p>
      </div>
    </div>
  );
};

export default OfflineLocationListContent;
